import React from "react";
import {
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
} from "react-icons/md";

export const Pagination = ({
  page,
  setPage,
  pageSize,
  setPageSize,
  total_items = 8,
  callbackFunction,
}: any) => {
  total_items = !total_items ? 0 : total_items;

  // Page math (display only)
  const pages = Math.ceil(total_items / pageSize || 1);
  const start = total_items === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = total_items === 0 ? 0 : Math.min(page * pageSize, total_items);

  const canPrev = page > 1;
  const canNext = page < pages;

  const options = [50, 100, 200, 300, 400, 500, 600, 7000, 800, 900, 1000, 1500, 2000];

  const ceiling = () => Math.ceil(total_items / pageSize);

  const onSizeChange = (e: any) => {
    const value = e?.target?.value;
    setPageSize(value);
    setPage(1);
    callbackFunction(1, value);
  };

  const goFirst = () => {
    if (page !== 1) {
      setPage(1);
      callbackFunction(1, pageSize);
    }
  };

  const goPrev = () => {
    if (page - 1 >= 1) {
      setPage(page - 1);
      callbackFunction(page - 1, pageSize);
    }
  };

  const goNext = () => {
    if (page + 1 <= ceiling()) {
      setPage(page + 1);
      callbackFunction(page + 1, pageSize);
    }
  };

  const goLast = () => {
    const last = ceiling();
    if (page !== last) {
      setPage(last);
      callbackFunction(last, pageSize);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Top: count + page size */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-gray-600">
          Showing{" "}
          <span className="font-semibold tabular-nums text-gray-900">
            {start.toLocaleString()}–{end.toLocaleString()}
          </span>{" "}
          of{" "}
          <span className="font-semibold tabular-nums text-gray-900">
            {total_items.toLocaleString()}
          </span>
        </p>

        <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
          <span>Rows</span>
          <select
            value={pageSize}
            onChange={onSizeChange}
            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-900 shadow-sm outline-none transition hover:border-gray-300 focus:border-brand focus:ring-2 focus:ring-brand/20"
          >
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Bottom: navigation */}
      <div className="flex items-center justify-center gap-1">
        <NavBtn onClick={goFirst} disabled={!canPrev} ariaLabel="First page">
          <MdFirstPage className="h-4 w-4" />
        </NavBtn>
        <NavBtn onClick={goPrev} disabled={!canPrev} ariaLabel="Previous page">
          <MdChevronLeft className="h-4 w-4" />
        </NavBtn>

        <span className="mx-1 inline-flex items-center gap-1 px-2 text-[11px] font-semibold tabular-nums">
          <span className="text-gray-900">{String(page).padStart(2, "0")}</span>
          <span className="text-gray-300">/</span>
          <span className="text-gray-500">
            {pages === 0 ? "01" : String(pages).padStart(2, "0")}
          </span>
        </span>

        <NavBtn onClick={goNext} disabled={!canNext} ariaLabel="Next page">
          <MdChevronRight className="h-4 w-4" />
        </NavBtn>
        <NavBtn onClick={goLast} disabled={!canNext} ariaLabel="Last page">
          <MdLastPage className="h-4 w-4" />
        </NavBtn>
      </div>
    </div>
  );
};

const NavBtn = ({
  onClick,
  disabled,
  ariaLabel,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    title={ariaLabel}
    className={[
      "inline-flex h-8 w-8 items-center justify-center rounded-lg border transition",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
      disabled
        ? "cursor-not-allowed border-gray-100 bg-white text-gray-300"
        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900",
    ].join(" ")}
  >
    {children}
  </button>
);
